import React, { useState, useMemo } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  KeyboardArrowDown,
  KeyboardArrowUp,
  AccountBalanceWallet,
  Edit,
  Close,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Person,
  Email,
  Phone,
  Receipt,
  ArrowUpward,
  ArrowDownward,
  CalendarToday,
  Visibility,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListWalletUsersQuery,
  useListWalletTransactionsQuery,
  WalletUser,
  WalletTransactionsRequest,
} from '../../features/admin/userWalletApi.ts';

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: Transaction dialog — fetches live data per user_id
// ─────────────────────────────────────────────────────────────────────────────
const TxnDialogContent: React.FC<{
  userId: number;
  formatCurrency: (v: string | number) => string;
  formatDate: (s: string) => string;
  getTransactionTypeColor: (type: string) => { bgcolor: string; color: string };
}> = ({ userId, formatCurrency, formatDate, getTransactionTypeColor }) => {

  const [txnPage, setTxnPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<'credit' | 'debit' | ''>('');
  const txnLimit = 10;

  const { data, isLoading, isError, refetch } = useListWalletTransactionsQuery(
    { user_id: userId, type: typeFilter || undefined, page: txnPage, limit: txnLimit },
    { refetchOnMountOrArgChange: true }
  );

  const transactions = data?.data || [];
  const totalTxns = data?.total || 0;
  const totalPages = Math.ceil(totalTxns / txnLimit);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} sx={{ color: '#e65100' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>
          Failed to load transactions. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {(['', 'credit', 'debit'] as const).map((t) => (
          <Chip
            key={t || 'all'}
            label={t === '' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
            onClick={() => { setTypeFilter(t); setTxnPage(1); }}
            sx={{
              cursor: 'pointer', fontWeight: 600, fontSize: '12px',
              bgcolor: typeFilter === t ? '#202223' : '#f1f2f4',
              color: typeFilter === t ? 'white' : '#6d7175',
              '&:hover': { bgcolor: typeFilter === t ? '#000' : '#e1e3e5' },
            }}
          />
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
          {totalTxns} transaction{totalTxns !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Transaction Table */}
      {transactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#fafbfb', borderRadius: '10px', border: '1px dashed #e1e3e5' }}>
          <Receipt sx={{ fontSize: 36, color: '#c9cccf', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">No transactions found</Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                  {['Txn ID', 'Type', 'Amount', 'Source', 'Order / Return', 'Date'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px', color: '#6d7175', textTransform: 'uppercase', py: 1.2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id} sx={{ '&:hover': { bgcolor: '#fafbfb' }, '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{txn.id}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {txn.type === 'credit'
                          ? <ArrowUpward sx={{ fontSize: 14, color: '#00783e' }} />
                          : <ArrowDownward sx={{ fontSize: 14, color: '#c62828' }} />
                        }
                        <Chip
                          label={txn.type}
                          size="small"
                          sx={{ ...getTransactionTypeColor(txn.type), fontWeight: 600, fontSize: '11px', height: '22px', textTransform: 'capitalize' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: txn.type === 'credit' ? '#00783e' : '#c62828' }}>
                        {txn.type === 'credit' ? '+' : '-'}{formatCurrency(txn.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Chip label={txn.source || '—'} size="small" sx={{ bgcolor: '#f1f2f4', color: '#6d7175', fontWeight: 500, fontSize: '11px', height: '22px' }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                        {txn.order_id ? `Order #${txn.order_id}` : ''}
                        {txn.order_id && txn.return_id ? ' · ' : ''}
                        {txn.return_id ? `Return #${txn.return_id}` : ''}
                        {!txn.order_id && !txn.return_id ? '—' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>{formatDate(txn.created_at)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={txnPage}
                onChange={(_, p) => setTxnPage(p)}
                size="small"
                sx={{ '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const UserWallet = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  // Transaction dialog
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [txnDialogUser, setTxnDialogUser] = useState<WalletUser | null>(null);

  // API
  const { data: walletResponse, isLoading, isError, refetch } = useListWalletUsersQuery({ search: debouncedSearch });

  const walletUsers: WalletUser[] = walletResponse?.data || [];
  const stats = walletResponse?.stats || { total_balance: 0, total_credited: 0, total_debited: 0, users_with_balance: 0 };

  const usersPerPage = 10;

  // ── Helpers ──
  const getUserInitials = (fn?: string | null, ln?: string | null) => {
    const f = fn?.trim() || ''; const l = ln?.trim() || '';
    if (!f && !l) return '?';
    if (!f) return l[0].toUpperCase();
    if (!l) return f[0].toUpperCase();
    return `${f[0]}${l[0]}`.toUpperCase();
  };

  const getUserFullName = (fn?: string | null, ln?: string | null) => {
    const f = fn?.trim() || ''; const l = ln?.trim() || '';
    if (!f && !l) return 'Unknown User';
    if (!f) return l; if (!l) return f;
    return `${f} ${l}`;
  };

  const formatCurrency = (amount: string | number): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `AED ${(num || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return dateStr; }
  };

  const getTransactionTypeColor = (type: string) => {
    if (type === 'credit') return { bgcolor: '#d1f4e0', color: '#00783e' };
    if (type === 'debit') return { bgcolor: '#ffebee', color: '#c62828' };
    return { bgcolor: '#f1f2f4', color: '#6d7175' };
  };

  const getBalanceColor = (balance: string | number) => {
    const b = typeof balance === 'string' ? parseFloat(balance) : balance;
    if (b > 1000) return '#00783e';
    if (b > 0) return '#e65100';
    return '#6d7175';
  };

  // ── Search with debounce ──
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(e.target.value);
    setPage(1);
    clearTimeout((window as any)._walletSearchTimer);
    (window as any)._walletSearchTimer = setTimeout(() => setDebouncedSearch(e.target.value), 400);
  };

  // ── Pagination (client-side fallback) ──
  const totalRecords = walletUsers.length;
  const totalPages = Math.ceil(totalRecords / usersPerPage);
  const paginatedUsers = walletUsers.slice((page - 1) * usersPerPage, page * usersPerPage);

  // ── Handlers ──
  const handleDrawerToggle = () => setMobileOpen(v => !v);
  const handleChangePage = (_: React.ChangeEvent<unknown>, p: number) => setPage(p);
  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));
  const clearFilters = () => { setSearchStr(''); setDebouncedSearch(''); setPage(1); };
  const handleToggleExpand = (id: number) => setExpandedUserId(v => v === id ? null : id);

  const handleRefresh = async () => {
    try { await refetch(); setSnackbar({ open: true, message: 'Refreshed!', severity: 'success' }); }
    catch { setSnackbar({ open: true, message: 'Error refreshing', severity: 'error' }); }
  };

  const handleOpenTxnDialog = (e: React.MouseEvent, user: WalletUser) => { e.stopPropagation(); setTxnDialogUser(user); setTxnDialogOpen(true); };
  const handleCloseTxnDialog = () => { setTxnDialogOpen(false); setTxnDialogUser(null); };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>

      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}>
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>User Wallet Management</Typography>
              <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}
                sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                Refresh
              </Button>
            </Box>

            {/* Stats */}
            {!isLoading && !isError && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Person sx={{ color: '#1565c0', fontSize: 24 }} />, bg: '#e3f2fd', value: stats.users_with_balance, label: 'Users with Balance', isCount: true },
                  { icon: <AccountBalanceWallet sx={{ color: '#7b1fa2', fontSize: 24 }} />, bg: '#f3e5f5', value: formatCurrency(stats.total_balance), label: 'Total Wallet Balance' },
                  { icon: <TrendingUp sx={{ color: '#2e7d32', fontSize: 24 }} />, bg: '#e8f5e9', value: formatCurrency(stats.total_credited), label: 'Total Credited' },
                  { icon: <TrendingDown sx={{ color: '#c62828', fontSize: 24 }} />, bg: '#ffebee', value: formatCurrency(stats.total_debited), label: 'Total Debited' },
                ].map(({ icon, bg, value, label, isCount }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', maxWidth: 320, width: '100%' }}>
                      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ bgcolor: bg, borderRadius: '8px', p: 1, display: 'flex' }}>{icon}</Box>
                        <Box>
                          <Typography variant={isCount ? 'h5' : 'h6'} sx={{ fontWeight: 600, color: '#202223', fontSize: isCount ? undefined : '18px' }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Table Card */}
            <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}
                  sx={{ px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' }, '& .Mui-selected': { color: '#202223', fontWeight: 600 }, '& .MuiTabs-indicator': { backgroundColor: '#202223', height: 3 } }}>
                  <Tab label={`All Wallet Users (${totalRecords})`} />
                </Tabs>
              </Box>

              <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Search by name, email, or contact..." value={searchStr} onChange={handleSearch} fullWidth
                  sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#6d7175' }} /></InputAdornment> }} />
                <Button variant="outlined" startIcon={<FilterList />} onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}>
                  Clear
                </Button>
              </Box>

              {/* States */}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                  <CircularProgress size={32} sx={{ color: '#008060' }} />
                </Box>
              )}
              {isError && !isLoading && (
                <Box sx={{ p: 3, bgcolor: 'white' }}><Alert severity="error">Failed to load wallet users. Please try again.</Alert></Box>
              )}
              {!isLoading && !isError && paginatedUsers.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                  <AccountBalanceWallet sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No wallet users found</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchStr ? `No results for "${searchStr}"` : 'No users have wallets yet'}
                  </Typography>
                </Box>
              )}

              {/* Table */}
              {!isLoading && paginatedUsers.length > 0 && (
                <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                  {/* Desktop Header */}
                  {!isMobile && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '48px 70px minmax(0, 1fr) 200px 140px 140px 140px 80px', minWidth: 1082, gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #e1e3e5', bgcolor: '#fafbfb', alignItems: 'center' }}>
                      {['', 'ID', 'User', 'Contact', 'Balance', 'Total Credited', 'Total Debited', 'Actions'].map(h => (
                        <Typography key={h} variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: h === 'Actions' ? 'center' : 'left' }}>{h}</Typography>
                      ))}
                    </Box>
                  )}

                  {paginatedUsers.map((user) => {
                    const userInitials = getUserInitials(user.first_name, user.last_name);
                    const userFullName = getUserFullName(user.first_name, user.last_name);
                    const balance = parseFloat(String(user.balance || '0'));

                    return (
                      <Box key={user.user_id}>
                        <Box
                          sx={!isMobile ? {
                            display: 'grid', gridTemplateColumns: '48px 70px minmax(0, 1fr) 200px 140px 140px 140px 80px', minWidth: 1082,
                            gap: 1.5, px: 2, py: 2, alignItems: 'center', borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease', cursor: 'pointer'
                          } : { p: 2, borderBottom: '1px solid #e1e3e5', '&:hover': { bgcolor: '#f9fafb' }, cursor: 'pointer' }}
                          onClick={() => handleToggleExpand(user.user_id)}
                        >
                          {!isMobile ? (
                            <>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175', p: 0 }}>
                                {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>#{user.user_id}</Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#f1f2f4', color: '#202223', fontSize: '14px', fontWeight: 600 }}>{userInitials}</Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>{userFullName}</Typography>
                              </Box>

                              <Box>
                                {user.user_email && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>{user.user_email}</Typography>}
                                {user.user_contact && <Typography variant="caption" color="text.secondary">{user.user_contact}</Typography>}
                              </Box>

                              <Typography variant="body2" sx={{ fontWeight: 700, color: getBalanceColor(balance) }}>{formatCurrency(balance)}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#00783e' }}>{formatCurrency(user.total_credited)}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#c62828' }}>{formatCurrency(user.total_debited)}</Typography>

                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <IconButton onClick={e => handleOpenTxnDialog(e, user)} size="small" title="View Transactions"
                                  sx={{ color: '#202223', bgcolor: '#f1f2f4', borderRadius: '6px', '&:hover': { bgcolor: '#e1e3e5' } }}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            /* Mobile card */
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ width: 40, height: 40, bgcolor: '#f1f2f4', color: '#202223', fontWeight: 600 }}>{userInitials}</Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{userFullName}</Typography>
                                    <Typography variant="caption" color="text.secondary">ID: {user.user_id}</Typography>
                                  </Box>
                                </Box>
                                <IconButton size="small" onClick={e => { e.stopPropagation(); handleToggleExpand(user.user_id); }} sx={{ color: '#6d7175' }}>
                                  {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: getBalanceColor(balance) }}>{formatCurrency(balance)}</Typography>
                                  <Typography variant="caption" color="text.secondary">Wallet Balance</Typography>
                                </Box>
                                <Button size="small" startIcon={<Visibility />} onClick={e => handleOpenTxnDialog(e, user)}
                                  sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500, color: '#202223' }}>
                                  Transactions
                                </Button>
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded Details */}
                        <Collapse in={expandedUserId === user.user_id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202223' }}>Wallet Overview</Typography>
                              <Button size="small" startIcon={<Receipt />} onClick={e => handleOpenTxnDialog(e, user)}
                                sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 500, color: '#202223', border: '1px solid #c9cccf' }}>
                                View Transactions
                              </Button>
                            </Box>

                            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                              <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" color="text.secondary">Wallet Balance</Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: getBalanceColor(balance), mt: 0.5 }}>{formatCurrency(balance)}</Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                    <ArrowUpward sx={{ fontSize: 14, color: '#00783e' }} />
                                    <Typography variant="caption" color="text.secondary">Total Credited</Typography>
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#00783e' }}>{formatCurrency(user.total_credited)}</Typography>
                                </Card>
                              </Grid>
                              <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                                <Card
                                  onClick={e => handleOpenTxnDialog(e, user)}
                                  sx={{
                                    p: 2, borderRadius: '8px', border: '1px solid #ffcdd2', bgcolor: '#fff8f8',
                                    cursor: 'pointer', transition: 'all 0.18s ease', flex: 1,
                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                    '&:hover': { bgcolor: '#ffebee',  boxShadow: '0 2px 8px rgba(198,40,40,0.12)', transform: 'translateY(-1px)' },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <ArrowDownward sx={{ fontSize: 14, color: '#c62828' }} />
                                      <Typography variant="caption" color="text.secondary">Total Debited</Typography>
                                    </Box>
                                    <Receipt sx={{ fontSize: 16, color: '#c62828', opacity: 0.7 }} />
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#c62828', mt: 0.5 }}>{formatCurrency(user.total_debited)}</Typography>
                               
                                </Card>
                              </Grid>
                            </Grid>

                            {/* Contact info */}
                            {(user.user_email || user.user_contact) && (
                              <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5', bgcolor: 'white', mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#202223' }}>Contact Information</Typography>
                                <Stack spacing={1.5}>
                                  {user.user_email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Email sx={{ color: '#6d7175', fontSize: 18 }} /><Typography variant="body2">{user.user_email}</Typography></Box>}
                                  {user.user_contact && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Phone sx={{ color: '#6d7175', fontSize: 18 }} /><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{user.user_contact}</Typography></Box>}
                                </Stack>
                              </Card>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Card>

            {totalPages > 1 && !isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={handleChangePage}
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px', fontWeight: 500 }, '& .Mui-selected': { bgcolor: '#202223 !important', color: 'white' } }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Transaction Dialog */}
      <Dialog open={txnDialogOpen} onClose={handleCloseTxnDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ bgcolor: '#f3e5f5', borderRadius: '8px', p: 0.8, display: 'flex' }}>
                <AccountBalanceWallet sx={{ color: '#7b1fa2', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>Wallet Transactions</Typography>
                {txnDialogUser && (
                  <Typography variant="caption" color="text.secondary">
                    {getUserFullName(txnDialogUser.first_name, txnDialogUser.last_name)} &nbsp;·&nbsp; ID #{txnDialogUser.user_id}
                    &nbsp;·&nbsp; Balance: <strong style={{ color: '#7b1fa2' }}>{formatCurrency(txnDialogUser.balance)}</strong>
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={handleCloseTxnDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {txnDialogUser && (
            <TxnDialogContent
              userId={txnDialogUser.user_id}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              getTransactionTypeColor={getTransactionTypeColor}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseTxnDialog} variant="contained"
            sx={{ textTransform: 'none', bgcolor: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { bgcolor: '#000' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserWallet;